/**
 * O que falta para o Caso estar completo (`TP-058`), em resumo: os dois níveis, o cadastro que falta e em que passo está
 * cada frente. O detalhe de cada passo mora no cartão da frente, que é onde se age; aqui só se aponta para ele. A conta é
 * a mesma da trava do passo e do cartão do quadro: se discordassem, ninguém confiaria em nenhum dos três.
 *
 * O valor do dado mora em "Informações do caso": aqui é o item, o estado e o porquê. A exceção é o valor que o e-mail
 * trouxe e ninguém confirmou (`a_confirmar`, `TP-072`): ele aparece ao lado do item com um check, porque ninguém confirma
 * sem ver o que está confirmando.
 *
 * O que é do cadastro, o nível e a camada de cada item vêm do checklist da operadora (`TP-059`). O opcional aparece
 * marcado e não conta; o Processo Inicial (os documentos depois da 1ª diária) tem linha própria, fora da conta.
 */

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ErroDaApi } from '../../../shared/api/erros';
import { documentosDoCaso } from '../documentos/api';
import { informacoesDoCaso } from '../informacoes/api';
import { AceiteSemProva } from './AceiteSemProva';
import { ConfirmarDoEmail } from './ConfirmarDoEmail';
import { chavesDeCompletude, completudeDoCaso } from './api';
import {
  ROTULO_DO_ESTADO,
  aceitavel,
  idDaFrente,
  textoDeFaltaDesde,
  textoDoMotivo,
  type ItemDaCompletude,
} from './tipos';

const ROTULO_DA_LINHA = 'w-20 shrink-0 text-[0.6875rem] uppercase tracking-[0.07em] text-[var(--tinta-3)]';
const LINK =
  'cursor-pointer border-0 bg-transparent p-0 text-[0.8125rem] underline decoration-dotted underline-offset-2';

function irParaFrente(token: string): void {
  document.getElementById(idDaFrente(token))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function faltam(n: number): string {
  return `falta${n === 1 ? '' : 'm'} ${String(n)}`;
}

/** Fabricado ou divergente não é só "falta": é dado que está lá e não se sustenta. */
function problema(item: ItemDaCompletude): boolean {
  return item.estado === 'fabricado' || item.estado === 'diverge';
}

export function OQueFaltaParaCompletar({ casoId }: { casoId: string }): React.JSX.Element {
  const consulta = useQuery(completudeDoCaso(casoId));
  const [aberto, setAberto] = useState<string | null>(null);

  // Documento que chega, é validado ou descartado muda o que falta, mas isso é escrita da própria pessoa, e a escrita já
  // relê a completude (`invalidarOQueAProvaMuda`). O que muda sem ninguém clicar é a leitura que termina: a visão de
  // documentos se relê a cada 15 s enquanto há leitura pendente, e a completude é relida quando um documento que estava
  // pendente deixa de estar. Só isso: relida a cada busca ou a cada mudança da visão, a releitura cancelaria a anterior
  // (com a completude mais lenta que o intervalo, nenhum pedido chegaria ao fim) e cada escrita a pediria duas vezes.
  // Fora disso, como o próprio Caso, ela se relê no foco da janela.
  const cliente = useQueryClient();
  const documentos = useQuery(documentosDoCaso(casoId));
  const pendentes = useMemo(
    () =>
      documentos.data?.documentos
        .filter((d) => d.leitura === 'pendente')
        .map((d) => d.id)
        .sort()
        .join(' ') ?? null,
    [documentos.data],
  );
  // A primeira visão de cada Caso não conta: trocar de Caso sem desmontar não é leitura que terminou.
  const visto = useRef<{ casoId: string; pendentes: readonly string[] } | null>(null);
  useEffect(() => {
    if (pendentes === null) return;
    const agora = pendentes === '' ? [] : pendentes.split(' ');
    const antes = visto.current;
    if (antes !== null && antes.casoId === casoId && antes.pendentes.some((id) => !agora.includes(id))) {
      // Só a completude: a leitura que termina não valida nada, então não fecha passo. Sem cancelar um pedido já em
      // curso, que é justamente o que deixava o painel sem carregar.
      void cliente.invalidateQueries({ queryKey: chavesDeCompletude.doCaso(casoId) }, { cancelRefetch: false });
    }
    visto.current = { casoId, pendentes: agora };
  }, [pendentes, cliente, casoId]);

  // O IW só responde em nome de uma pessoa: ler as informações do Caso é o que grava o que ele provou do cadastro
  // (`TP-072`). A completude se relê só quando o servidor diz que a leitura mudou a prova (`provaNova`): abrir o Caso de
  // novo, com o IW dizendo o mesmo, não pede a completude duas vezes.
  const informacoes = useQuery(informacoesDoCaso(casoId));
  const provaNovaEm = informacoes.data?.iw.provaNova === true ? informacoes.dataUpdatedAt : null;
  const provaVista = useRef<number | null>(null);
  useEffect(() => {
    if (provaNovaEm === null || provaVista.current === provaNovaEm) return;
    provaVista.current = provaNovaEm;
    void cliente.invalidateQueries({ queryKey: chavesDeCompletude.doCaso(casoId) }, { cancelRefetch: false });
  }, [provaNovaEm, cliente, casoId]);

  if (consulta.isPending) return <p className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">Carregando o que falta…</p>;
  if (consulta.isError) {
    return (
      <p className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">
        {consulta.error instanceof ErroDaApi && consulta.error.status === 503
          ? 'O que falta precisa do banco, e ele não está ligado neste ambiente.'
          : 'Não deu para carregar o que falta.'}
      </p>
    );
  }

  const completude = consulta.data;
  // Um nível por vez: enquanto falta o mínimo para abrir, só ele aparece; aberto, ele some e aparece o que falta para
  // completar.
  const paraAbrir = !completude.nivelUmCompleto;
  const cadastro = completude.itens.filter(
    (i) =>
      i.pendente &&
      !i.passo &&
      i.camada === 'cadastro' &&
      i.onde.some((o) => o.frente === null) &&
      (!paraAbrir || (i.nivel === 1 && !i.opcional)),
  );
  const cadastroQueConta = cadastro.filter((i) => !i.opcional);
  // Só aparece depois da autorização: antes, o back nem os manda.
  const processoInicial = completude.itens.filter((i) => i.camada === 'processo_inicial');
  const processoPendente = processoInicial.filter((i) => i.pendente);
  const frentes = paraAbrir
    ? []
    : completude.frentes.filter((f) => f.passosPorFazer > 0 || f.itensFaltando > 0);
  const itemAberto = cadastro.find((i) => i.chave === aberto) ?? null;
  const motivoAberto = itemAberto === null ? null : textoDoMotivo(itemAberto.motivo);

  return (
    <div className="space-y-2 text-[0.8125rem]">
      {completude.faltam === 0 ? (
        <p className="m-0 text-[var(--tinta)]">Nada falta: o checklist deste caso está completo.</p>
      ) : (
        <p className="m-0 flex flex-wrap items-baseline gap-x-2 text-[var(--tinta)]">
          {paraAbrir ? (
            <span>
              <b>Para abrir</b>, falta o mínimo do cadastro.
            </span>
          ) : (
            <span>
              <b>Para completar:</b> {faltam(completude.faltam)}
            </span>
          )}
          {completude.nivelProvisorio && (
            <span
              className="text-[0.6875rem] text-[var(--tinta-3)]"
              title="O que basta para abrir e o que é preciso para completar ainda espera a confirmação da Anery."
            >
              (corte provisório)
            </span>
          )}
          {!completude.checklist.configurado && (
            <span
              className="text-[0.6875rem] text-[var(--tinta-3)]"
              title="Esta operadora não tem checklist próprio: vale o padrão (nome, apelido, data de nascimento e filial para abrir)."
            >
              (checklist da operadora não configurado: valendo o padrão)
            </span>
          )}
        </p>
      )}

      {cadastroQueConta.length > 0 && (
        <div className="flex items-baseline gap-2">
          <span className={ROTULO_DA_LINHA}>Cadastro</span>
          <ul aria-label="Falta no cadastro" className="m-0 flex min-w-0 flex-wrap gap-x-3 gap-y-1 p-0">
            {cadastro.map((item) => (
              <li key={item.chave} className="list-none">
                <button
                  type="button"
                  aria-expanded={aberto === item.chave}
                  title={ROTULO_DO_ESTADO[item.estado]}
                  className={`${LINK} ${problema(item) ? 'text-[var(--roxo)]' : 'text-[var(--tinta)]'}`}
                  onClick={() => {
                    setAberto((atual) => (atual === item.chave ? null : item.chave));
                  }}
                >
                  {item.rotulo}
                  {item.opcional && <span className="text-[var(--tinta-3)]"> (opcional)</span>}
                </button>
                {item.motivo === 'a_confirmar' && item.sugestoes[0] !== undefined && (
                  <>
                    {' '}
                    <ConfirmarDoEmail casoId={casoId} rotulo={item.rotulo} sugestao={item.sugestoes[0]} />
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Cadastro todo provado: é a hora de levar o que foi provado ao IW. A escrita no IW ainda não existe no back, e
          o botão diz isso em vez de fingir que manda. */}
      {!paraAbrir && cadastroQueConta.length === 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className={ROTULO_DA_LINHA}>Cadastro</span>
          <span className="text-[var(--tinta)]">
            completo
            {/* O opcional que falta não segura o cadastro, mas continua à vista. */}
            {cadastro.length > 0 && (
              <span className="text-[var(--tinta-3)]"> · opcional: {cadastro.map((i) => i.rotulo).join(', ')}</span>
            )}
          </span>
          <Button
            size="small"
            variant="outlined"
            disabled
            title="Levar ao IW o cadastro provado. A escrita no IW ainda não existe."
          >
            Atualizar no IW
          </Button>
          <Chip size="small" label="Em breve" />
        </div>
      )}

      {itemAberto !== null && (
        <div className="ml-[5.5rem] rounded border border-solid border-[var(--fio)] bg-[var(--chao-2)] px-3 py-2">
          <p className="m-0 text-[var(--tinta-2)]">
            <b className="text-[var(--tinta)]">{itemAberto.rotulo}</b> · {ROTULO_DO_ESTADO[itemAberto.estado]}
            {motivoAberto !== null && <> · {motivoAberto}</>}
            {itemAberto.faltaDesde !== null && <> · {textoDeFaltaDesde(itemAberto.faltaDesde)}</>}
          </p>
          {aceitavel(itemAberto) && <AceiteSemProva casoId={casoId} chave={itemAberto.chave} />}
        </div>
      )}

      {processoInicial.length > 0 && (
        <div className="flex items-baseline gap-2">
          <span className={ROTULO_DA_LINHA}>Proc. inicial</span>
          <span
            className="text-[var(--tinta)]"
            title={processoPendente.map((i) => i.rotulo).join(' · ')}
          >
            {processoPendente.length === 0
              ? 'montado'
              : `${String(processoPendente.length)} de ${String(processoInicial.length)} documentos a montar`}
          </span>
        </div>
      )}

      {frentes.length > 0 && (
        <div className="flex items-baseline gap-2">
          <span className={ROTULO_DA_LINHA}>Por frente</span>
          <ul aria-label="Falta nas frentes" className="m-0 flex min-w-0 flex-wrap gap-x-3 gap-y-1 p-0">
            {frentes.map((f) => (
              <li key={f.frente} className="list-none">
                <button
                  type="button"
                  className={`${LINK} text-[var(--tinta)]`}
                  title={`passo da vez: ${String(f.passoDaVez)} · ${faltam(f.itensFaltando)} ${f.itensFaltando === 1 ? 'item' : 'itens'}`}
                  onClick={() => {
                    irParaFrente(f.frente);
                  }}
                >
                  {/* O passo da vez, o traço amarelo da barra do cartão. Os itens moram dentro dos passos. */}
                  {f.rotulo}: passo {f.passoDaVez} de {f.totalDePassos}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
